import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiSend,
  FiPaperclip,
  FiMapPin,
} from "react-icons/fi";
import { MessageSquareDashed, MessagesSquare } from "lucide-react";
import {
  listMessages,
  sendMessage,
  listMyMissions,
  getMission,
  typeLabel,
  formatDateTime,
  assetUrl,
} from "../../utils/missionsApi";
import MissionTypeIcon from "./components/MissionTypeIcon";

export default function MissionMessages() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const [mission, setMission] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [threads, setThreads] = useState([]);
  const userId = localStorage.getItem("userId");

  const inputRef = useRef(null);
  const scrollerRef = useRef(null);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        listMyMissions("created").catch(() => ({ items: [] })),
        listMyMissions("assigned").catch(() => ({ items: [] })),
      ]);
      const merged = [...(a.items || []), ...(b.items || [])]
        .filter((m, idx, arr) => arr.findIndex((x) => x._id === m._id) === idx)
        .filter((m) => m.assignedTo); // only threads with provider+client
      setThreads(merged);
    } finally { setLoading(false); }
  }, []);

  const loadThread = useCallback(async () => {
    setLoading(true);
    try {
      const m = await getMission(paramId);
      setMission(m);
      const { items } = await listMessages(paramId);
      setMessages(items || []);
    } finally { setLoading(false); }
  }, [paramId]);

  useEffect(() => {
    if (paramId) loadThread();
    else loadThreads();
  }, [paramId, loadThread, loadThreads]);

  useEffect(() => {
    // Auto scroll
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages]);

  // Poll while open
  useEffect(() => {
    if (!paramId) return undefined;
    const id = setInterval(() => {
      listMessages(paramId).then(({ items }) => {
        setMessages(items || []);
      }).catch(() => {});
    }, 8000);
    return () => clearInterval(id);
  }, [paramId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() && !attachments.length) return;
    setSending(true);
    try {
      await sendMessage(paramId, { text, attachments });
      setText("");
      setAttachments([]);
      const { items } = await listMessages(paramId);
      setMessages(items || []);
    } catch (err) {
      alert(err?.response?.data?.message || "Erreur");
    } finally { setSending(false); }
  };

  const sharePosition = () => {
    if (!navigator.geolocation) return alert("Géolocalisation non supportée");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        await sendMessage(paramId, {
          text: "📍 Position partagée",
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: "Ma position",
        });
        const { items } = await listMessages(paramId);
        setMessages(items || []);
      } catch (err) {
        alert("Erreur partage position");
      }
    });
  };

  // ===== Thread list view =====
  if (!paramId) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div className="pmm-topbar">
          <div>
            <h1 className="pmm-h-accent">Messages</h1>
            <div className="pmm-topbar-sub">Discussion privée avec le prestataire ou le client.</div>
          </div>
        </div>

        {loading ? <div className="pmm-spinner" />
          : threads.length === 0 ? (
            <div className="pmm-empty">
              <div className="pmm-empty-icon"><MessagesSquare size={28} /></div>
              <h3>Aucune conversation</h3>
              <p>Une conversation est ouverte dès qu'une mission est attribuée.</p>
            </div>
          ) : (
            <div className="pmm-grid pmm-grid--2">
              {threads.map((m) => (
                <article className="pmm-mission-card" key={m._id}>
                  <div className="pmm-mission-card__head">
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", minWidth: 0, flex: 1 }}>
                      <MissionTypeIcon type={m.type} size={38} />
                      <div style={{ minWidth: 0 }}>
                        <h3 className="pmm-mission-card__title">{m.title}</h3>
                        <div className="pmm-mission-card__type">{typeLabel(m.type)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="pmm-mission-card__actions">
                    <button className="pmm-btn pmm-btn--primary" onClick={() => navigate(`/missions/${m._id}/messages`)}>
                      <MessagesSquare size={14} /> Ouvrir la conversation
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
      </div>
    );
  }

  // ===== Thread view =====
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "calc(100vh - 120px)" }}>
      <div className="pmm-topbar" style={{ flex: "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button className="pmm-btn" onClick={() => navigate("/missions/messages")}><FiArrowLeft /> Retour</button>
          {mission && <MissionTypeIcon type={mission.type} size={44} />}
          <div>
            <h1 style={{ fontSize: 18 }}>{mission ? mission.title : "Discussion"}</h1>
            {mission && (
              <div className="pmm-topbar-sub">{typeLabel(mission.type)} • {mission.city}</div>
            )}
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="pmm-card"
        style={{
          flex: 1, overflowY: "auto", padding: 14, background: "linear-gradient(180deg, #f8fafc, #ffffff)",
          display: "flex", flexDirection: "column", gap: 10,
        }}
      >
        {messages.length === 0 && (
          <div className="pmm-empty" style={{ margin: "auto", padding: 24 }}>
            <div className="pmm-empty-icon"><MessageSquareDashed size={28} /></div>
            <h3>Démarrez la conversation</h3>
            <p>Échangez photos, position et infos avec votre interlocuteur.</p>
          </div>
        )}

        {messages.map((m) => {
          const own = String(m.senderId) === String(userId);
          return (
            <div
              key={m._id}
              style={{
                alignSelf: own ? "flex-end" : "flex-start",
                maxWidth: "78%",
                background: own ? "linear-gradient(135deg, var(--color-primary), var(--color-primary-hover))" : "#fff",
                color: own ? "#fff" : "var(--espresso)",
                border: own ? "none" : "1px solid var(--border)",
                borderRadius: own ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                padding: "10px 12px",
                boxShadow: own ? "var(--shadow-brand)" : "var(--shadow-sm)",
                whiteSpace: "pre-wrap",
              }}
            >
              {m.text && <div style={{ fontSize: 14 }}>{m.text}</div>}

              {m.attachments?.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 6, marginTop: 6 }}>
                  {m.attachments.map((a, i) => (
                    <a key={i} href={assetUrl(a.url)} target="_blank" rel="noreferrer">
                      <img src={assetUrl(a.url)} alt="" style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 8 }} />
                    </a>
                  ))}
                </div>
              )}

              {m.location?.coordinates?.length === 2 && (
                <a
                  href={`https://www.google.com/maps?q=${m.location.coordinates[1]},${m.location.coordinates[0]}`}
                  target="_blank" rel="noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 6, color: own ? "#e0f2fe" : "var(--color-primary-hover)", textDecoration: "underline" }}
                >
                  <FiMapPin /> Voir position
                </a>
              )}

              <div style={{ fontSize: 11, marginTop: 6, opacity: 0.75 }}>
                {formatDateTime(m.createdAt)}
              </div>
            </div>
          );
        })}
      </div>

      {attachments.length > 0 && (
        <div className="pmm-card" style={{ padding: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {attachments.map((f, i) => (
            <span key={i} className="pmm-chip pmm-chip--neutral">📎 {f.name}</span>
          ))}
          <button className="pmm-btn" onClick={() => setAttachments([])}>Effacer</button>
        </div>
      )}

      <form
        onSubmit={submit}
        className="pmm-card"
        style={{
          padding: 10, display: "flex", gap: 8, alignItems: "center", flex: "none",
          boxShadow: "0 -2px 12px rgba(15, 23, 42, 0.06)", borderRadius: 16,
        }}
      >
        <button
          type="button"
          className="pmm-btn"
          onClick={() => inputRef.current?.click()}
          aria-label="Joindre"
        >
          <FiPaperclip />
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            setAttachments((prev) => [...prev, ...files].slice(0, 6));
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className="pmm-btn"
          onClick={sharePosition}
          aria-label="Position"
          title="Partager ma position"
        >
          <FiMapPin />
        </button>
        <input
          type="text"
          placeholder="Écrivez un message…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{ flex: 1, minHeight: 44, padding: "10px 14px", borderRadius: 12, border: "1px solid var(--border-strong)" }}
        />
        <button type="submit" className="pmm-btn pmm-btn--primary" disabled={sending}>
          {sending ? "…" : <FiSend />}
        </button>
      </form>
    </div>
  );
}
